import React, { CSSProperties, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useServiceHook from "@hooks/useServiceHook";
import { SubscriptionService } from "@services/index";
import SubscriptionsListPanel from '@components/panels/SubscriptionsList/SubscriptionsList';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import { ISubscriptionSummaryItem } from "@components/cards/SubscriptionSummary/SubscriptionSummary";
import { InfoBox, WarningBox } from '@components/cards/messageBox';
import * as Sentry from "@sentry/react";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import { VehicleService } from "@services/Vehicles/Vehicles";
import { StorageService } from "@utils/sessionStorage";
import LoadingSpinner from "@utils/loadingSpinner";
import {SelectedSubscriptionState, SelectedSubscriptionAction} from "@views/VehicleExchange/selectedSubscriptionReducer";

type Props = {
  subscriptionState: SelectedSubscriptionState;
  subscriptionStateDispatch: React.Dispatch<SelectedSubscriptionAction>;
  navigateTarget: string;
};


export default function ExchangeSubscriptionsList({
  subscriptionState,
  subscriptionStateDispatch,
  navigateTarget
}: Props) {
  const navigate = useNavigate();
  const { custId, country } = useParams() as { custId: string; country: string };
  const [loading_sub_clicked, set_loading_sub_clicked] = useState<boolean>(false);


  Sentry.setTag("Exchange", "Exchange");
  Sentry.setTag("Start Time", new Date().toString());
  Sentry.setTag(`timed`, true);

  // Get customers subscriptions
  const [loading, error, errMessage, getSubscriptionResults] = useServiceHook(
    SubscriptionService.getSubscriptions,
    [custId, country],
    true,
    [custId, country]
  );

  const parseSubsPlan = (subs: ISubscriptionSummaryItem[]) => {
    return subs.map((subs) => {
      return subs;
    });
  };

  const subscriptions = loading === false ? parseSubsPlan(getSubscriptionResults) : [];
  const allInactive = subscriptions.every(subs => subs.status === 'Inactive');

  useEffect(() => {
    breadcrumbObject['Exchanges']['Exchange Start Time'] = new Date();
  }, [getSubscriptionResults]);

  const selectSubscriptionLabelStyle: CSSProperties = {
    margin: '0',
    padding: '2vh 0 2vh 2.5vw',
    color: '#2C2A2D',
    fontWeight: '700',
    borderBottom: '1px solid #C4C4C4'
  };

  const isSubscriptionClickable = (subs: ISubscriptionSummaryItem): boolean => {
    return subs.status !== "Inactive";
  };

  return (
    <>
      <NavBar pageTitle="Exchange" />
      <p style={selectSubscriptionLabelStyle}>
        Please select a subscription:
      </p>
      {loading ? (
        <LoadingSpinner testId="loading-spinner"/>
      ) : subscriptions.length > 0 ? (
          loading_sub_clicked ? (
                  <div style={{display: 'flex', justifyContent: 'center', margin: '15vh'}}>
                    <LoadingSpinner testId="loading-spinner"/>
                  </div>
              ) :
              <>
                <SubscriptionsListPanel
                    keyName="subscriptions"
                    items={subscriptions.map((subs) => ({
                      subsId: subs.subsId,
                      startDate: subs.startDate,
                      carDetails: subs.carDetails,
                      registration: subs.registration,
                      plan: subs.plan,
                      status: subs.status,
                      vehicleId: subs.vehicleId,
                      noticeGiven: subs.noticeGiven,
                      onClickFn: isSubscriptionClickable(subs)
                          ? () => {
                            console.debug("Subscription selected: " + subs.subsId);
                            set_loading_sub_clicked(true);
                            subscriptionStateDispatch({
                              type: "SELECT_SUBSCRIPTION",
                              payload: {
                                subsId: subs.subsId,
                                vehicleId: subs.vehicleId ?? "",
                                selectedPlan: subs.plan,
                                temporaryVehicleId: subs.tempVehicleId
                              },
                            });
                            navigate(navigateTarget);

                          }
                          : () => {
                            console.log("Subscription is inactive, cannot select");
                          },
                    }))}
                />
                <div style={{margin: '1.5vh 3.5vw'}}>
                  {allInactive && (
                      <WarningBox message="Please note that exchanges can only be processed for active subscriptions"/>
                  )}
                </div>
              </>
      ) : (
          <NoSubscriptionsInfoNote/>
      )}
    </>
  );
}

const NoSubscriptionsInfoNote = () => (
  <div style={{ margin: '1.5vh 3.5vw' }}>
    <InfoBox message="Please note that the customer does not have any subscriptions on their record" />
  </div>
);
